import { Component, OnDestroy, Renderer2 } from '@angular/core';
import { ChatStateService } from '../services/chat-state.service';
import { CommonModule } from '@angular/common';
import {
  combineLatest,
  concatMap,
  from,
  interval,
  map,
  Subscription,
  timer,
} from 'rxjs';
import {
  AiContent,
  ChatContentService,
} from '../services/chat-content.service';

type ChatStatus = {
  position: number;
  level: number;
  min: number;
  max: number;
  description: string;
};

@Component({
  selector: 'app-ai-relationships-bar',
  templateUrl: './ai-relationships-bar.component.html',
  styleUrls: ['./ai-relationships-bar.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class AiRelationshipsBarComponent implements OnDestroy {
  private readonly _totalWidth = 18;
  private readonly _tickWidth = 0.5;
  private readonly _hintDuration = 5000;

  public readonly statuses: ChatStatus[];

  private _subscriptions: Subscription[] = [];

  public rating$ = this._chatStateService.aiChatRating$.pipe(
    map((x) => x.rating),
  );

  public fillWidth$ = this.rating$.pipe(
    map((rating) => {
      const levelWidth = this._totalWidth / this.statuses.length;

      const levelIndex =
        this.statuses.findIndex((status) => status.min > rating) - 1;

      const status =
        this.statuses[levelIndex] ?? this.statuses[this.statuses.length - 1];

      const progressInLevel =
        (rating - status.min) / (status.max + 1 - status.min);

      return (
        levelWidth * (status.level + 1) +
        this._tickWidth / 2 +
        progressInLevel * (levelWidth - this._tickWidth)
      );
    }),
  );

  public fillWidthSmooth$ = combineLatest([this.fillWidth$, interval(16)]).pipe(
    map(([target, _]) => {
      const current = this.currentSmoothValue || target;
      this.currentSmoothValue = current + (target - current) * 0.005;
      return this.currentSmoothValue;
    }),
  );

  private currentSmoothValue: number | null = null;

  public currentStatus$ = this.fillWidthSmooth$.pipe(
    map((position) => {
      const levelIndex =
        this.statuses.findIndex((status) => status.position > position) - 1;

      return (
        this.statuses[levelIndex] ?? this.statuses[this.statuses.length - 1]
      );
    }),
  );

  private _hintTimeOut: ReturnType<typeof setTimeout> | null = null;
  public isHintEnabled = false;
  public hintedLevel: ChatStatus | null = null;
  public startRating: number;

  constructor(
    private _chatStateService: ChatStateService,
    private _chatContentService: ChatContentService,
    private _renderer: Renderer2,
  ) {
    const aiChatRating = _chatStateService.aiChatRating$.value;

    this.startRating = aiChatRating.rating;

    this.statuses = aiChatRating.levels.map((level, i) => ({
      ...level,
      position: (i + 1) * (this._totalWidth / aiChatRating.levels.length),
    }));

    this._subscriptions.push(
      this._chatContentService.contentAdded$
        .pipe(
          concatMap((contents) =>
            from(contents).pipe(
              concatMap((content) =>
                timer(200).pipe(
                  concatMap(() => {
                    this.showContentNotification(content);
                    return timer(200);
                  }),
                ),
              ),
            ),
          ),
        )
        .subscribe(),
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach((s) => s.unsubscribe());
  }

  public onStatusClick(level: ChatStatus) {
    if (this._hintTimeOut) {
      clearTimeout(this._hintTimeOut);
    }

    this._hintTimeOut = setTimeout(() => {
      this.isHintEnabled = false;
    }, this._hintDuration);

    this.hintedLevel = level;
    this.isHintEnabled = true;
  }

  public test() {
    // this.showContentNotification({
    //   index: 0,
    //   contentType: 'Video',
    //   ratingRequired: 0,
    //   url: 'https://cdn.getmynudes.com/chat-images/chloe-test/250716145159940307037-226b7609-02e4-4ee2-87c7-7a3a81bb9f7a.mp4',
    // });
    this.showContentNotification({
      index: 0,
      contentType: 'Image',
      ratingRequired: 0,
      url: 'https://cdn.getmynudes.com/chat-images/sophina-test/av-14d98372-fc46-4c44-a469-c24501bccb27.jpg',
    });
  }

  public async showContentNotification(content: AiContent) {
    const parent = document.body;
    const startEl = document.querySelector('.relationships-bar .heart');
    const target = document.querySelector('#gallery-tab-btn');

    const thumb = this._renderer.createElement('div');
    this._renderer.setStyle(thumb, 'opacity', '0');

    if (content.contentType === 'Video') {
      const video = this._renderer.createElement('video');
      this._renderer.appendChild(thumb, video);
      video.src = content.url;
      video.muted = true;
      video.autoplay = false;
      video.loop = false;
      video.playsInline = true;

      await new Promise((resolve) => {
        video.addEventListener('loadedmetadata', resolve, { once: true });
      });
    } else {
      const image = this._renderer.createElement('img');
      image.src = content.url;
      this._renderer.appendChild(thumb, image);

      await new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
      });
    }

    this._renderer.addClass(thumb, 'thumb');
    this._renderer.appendChild(parent, thumb);
    this._renderer.removeStyle(thumb, 'opacity');

    const start = startEl.getBoundingClientRect();
    const end = target.getBoundingClientRect();

    const startX = start.left + start.width / 2;
    const startY = start.top + start.height / 2;
    const endX = end.left + end.width / 2;
    const endY = end.top + end.height / 2;

    const controlX = startX;
    const controlY = (startY - endY) / 3;

    const path = `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;

    this._renderer.setStyle(thumb, 'offsetPath', `path("${path}")`);

    thumb.addEventListener('animationend', () => {
      this._renderer.removeChild(parent, thumb);
    });
  }
}
